import { getDaysInMonth } from 'date-fns';
import * as Dfns from 'date-fns/fp';
import { useCallback, useMemo, useState } from 'react';
import {
  CompanyFiscalCal,
  FiscalCalType,
  FiscalPatternCal,
  getMonthLabelPattern,
  getQuarterLabelPattern,
  getQuarterLabelYearly
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { convertDateToUTC } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { MILLISECONDS_IN_A_DAY } from '../../utils/date-utils';

export const headerFormatter = (
  dateGrouping: string | undefined,
  label: string,
  fiscalCal: CompanyFiscalCal
): string => {
  if (!label || !Dfns.isValid(Dfns.parseISO(label))) {
    return '';
  }
  const { calType, seedDate, generatedPatternCal } = fiscalCal || {};
  let formattedLabel;
  let dateToUse = Dfns.parseISO(label);

  switch (dateGrouping) {
    case 'Day':
      formattedLabel = Dfns.format('M/d/yyyy', dateToUse);
      break;
    case 'Week':
      formattedLabel = `Week of ${Dfns.format('M/d/yyyy', dateToUse)}`;
      break;
    case 'Month':
      switch (calType) {
        case FiscalCalType.PATTERN: {
          dateToUse = getMonthLabelPattern(label, generatedPatternCal as FiscalPatternCal);
          break;
        }
        default:
          break;
      }
      formattedLabel = Dfns.format('MMMM', dateToUse);
      break;
    case 'Quarter': {
      switch (calType) {
        case FiscalCalType.YEARLY: {
          dateToUse = getQuarterLabelYearly(label, seedDate as string);
          break;
        }
        case FiscalCalType.PATTERN: {
          dateToUse = getQuarterLabelPattern(label, generatedPatternCal as FiscalPatternCal);
          break;
        }
        default:
          break;
      }
      formattedLabel = `Q${Dfns.getQuarter(Dfns.addDays(1, dateToUse))}, ${Dfns.format(
        'yyyy',
        Dfns.addDays(1, dateToUse)
      )}`;
      break;
    }
    default:
      break;
  }
  return formattedLabel;
};

const RECHART_CARTESIAN_YAXIS_TICK_VALUE_SELECTOR =
  '.recharts-cartesian-axis-tick-value[orientation="left"], .recharts-cartesian-axis-tick-value[orientation="right"]';
const RECHART_CARTESIAN_XAXIS_TICK_VALUE_SELECTOR =
  '.recharts-cartesian-axis-tick-value[orientation="bottom"], .recharts-cartesian-axis-tick-value[orientation="top"]';
const RECHART_CARTESIAN_XAXIS_SELECTOR = '.recharts-cartesian-axis.recharts-xAxis.xAxis';
const RECHART_CARTESIAN_YAXIS_SELECTOR = '.recharts-cartesian-axis.recharts-yAxis.yAxis';

interface DynamicYAxisWidthProps {
  xAxisHeightModifier?: (n: number) => number;
  yAxisWidthModifier?: (n: number) => number;
  reCalcDependency?: unknown;
  reCalcDepList?: unknown[];
  /** When provided and > 0, skip layout measurement (caller uses fixed width) */
  yAxisStaticWidth?: number;
}

interface ReturnValues {
  setChartRef: (chartRef: unknown) => void;
  xAxisHeight: number;
  xAxisWidth: number;
  yAxisWidth: number;
  yAxisHeight: number;
}

const DEFAULT_Y_AXIS_WIDTH = 40;
const DEFAULT_X_AXIS_HEIGHT = 30;
const INITIAL_X_AXIS_WIDTH = 0;
const INITIAL_Y_AXIS_HEIGHT = 0;

export const useDynamicYAxisWidth = ({
  xAxisHeightModifier,
  yAxisWidthModifier,
  reCalcDependency,
  reCalcDepList = [],
  yAxisStaticWidth
}: DynamicYAxisWidthProps): ReturnValues => {
  const [xAxisHeightState, setXAxisHeightState] = useState(DEFAULT_X_AXIS_HEIGHT);
  const [yAxisWidthState, setYAxisWidthState] = useState(DEFAULT_Y_AXIS_WIDTH);
  const [xAxisWidth, setXAxisWidth] = useState(INITIAL_X_AXIS_WIDTH);
  const [yAxisHeight, setYAxisHeight] = useState(INITIAL_Y_AXIS_HEIGHT);

  const setChartRef = useCallback(
    chartRef => {
      // recharts v2 exposed the DOM wrapper as chartRef.container; v3 passes the
      // container DOM node directly as the ref. Support both shapes.
      const container = chartRef == null ? null : (chartRef.container ?? chartRef);
      if (
        container != null &&
        typeof container.querySelectorAll === 'function' &&
        (yAxisStaticWidth == null || yAxisStaticWidth <= 0)
      ) {
        const yTickValueElements = container.querySelectorAll(
          RECHART_CARTESIAN_YAXIS_TICK_VALUE_SELECTOR
        );
        const highestWidth = [...yTickValueElements]
          .map(el => {
            const boundingRect = el.getBoundingClientRect();
            if (boundingRect != null && boundingRect.width != null) {
              return boundingRect.width;
            }
            return 0;
          })
          .reduce((accumulator, value) => {
            if (accumulator < value) {
              return value;
            }
            return accumulator;
          }, 0);
        // Only adopt a measured width when the ticks actually rendered. If this ref
        // callback fires before the chart has painted its y-axis ticks, the measurement
        // is 0 — writing that back collapses the axis to ~0px so the labels never get
        // room to render (they stay measured at 0 forever). Keep the current width
        // (default DEFAULT_Y_AXIS_WIDTH) so ticks paint, then a later pass measures them.
        if (highestWidth > 0) {
          setYAxisWidthState(highestWidth);
        }

        const xTickValueElements = container.querySelectorAll(
          RECHART_CARTESIAN_XAXIS_TICK_VALUE_SELECTOR
        );
        const highestHeight = [...xTickValueElements]
          .map(el => {
            const boundingRect = el.getBoundingClientRect();
            if (boundingRect != null && boundingRect.height != null) {
              return boundingRect.height;
            }
            return 0;
          })
          .reduce((accumulator, value) => {
            if (accumulator < value) {
              return value;
            }
            return accumulator;
          }, 0);
        // Only adopt a measured height when the ticks actually rendered — mirrors the
        // y-axis-width guard above. If this ref callback fires before the x-axis ticks
        // paint, the measurement is 0; writing that back collapses the reserved band to
        // ~21px, not enough for the tick text plus the axis label, so the label rides up
        // onto the numbers. Keep the current height until a later pass measures.
        if (highestHeight > 0) {
          setXAxisHeightState(highestHeight);
        }

        const xAxisElements = container.querySelectorAll(RECHART_CARTESIAN_XAXIS_SELECTOR);
        if (xAxisElements.length === 1) {
          const el = xAxisElements[0];
          const boundingRect = el.getBoundingClientRect();
          if (boundingRect != null && boundingRect.width != null) {
            setXAxisWidth(boundingRect.width);
          }
        }

        const yAxisElements = container.querySelectorAll(RECHART_CARTESIAN_YAXIS_SELECTOR);
        if (yAxisElements.length === 1) {
          const el = yAxisElements[0];
          const boundingRect = el.getBoundingClientRect();
          if (boundingRect != null && boundingRect.height != null) {
            setYAxisHeight(boundingRect.height);
          }
        }
      }
    },
    // add data as a dependency to force ref to update
    // eslint-disable-next-line
    [
      setYAxisWidthState,
      setXAxisHeightState,
      setXAxisWidth,
      setYAxisHeight,
      reCalcDependency,
      yAxisStaticWidth,
      // eslint-disable-next-line
      ...reCalcDepList
    ]
  );

  const yAxisWidth = useMemo(() => {
    if (yAxisWidthModifier != null && yAxisWidthState != null) {
      return yAxisWidthModifier(yAxisWidthState);
    }
    return yAxisWidthState;
  }, [yAxisWidthModifier, yAxisWidthState]);

  const xAxisHeight = useMemo(() => {
    if (xAxisHeightModifier != null && xAxisHeightState != null) {
      return xAxisHeightModifier(xAxisHeightState);
    }
    return xAxisHeightState;
  }, [xAxisHeightModifier, xAxisHeightState]);

  return {
    xAxisWidth,
    xAxisHeight,
    yAxisWidth,
    yAxisHeight,
    setChartRef
  };
};

export const buildEquidistantScale: (
  dataMin: number,
  dataMax: number,
  tickCount: number
) => { domain: [number, number]; ticks: number[] } = (dataMin, dataMax, tickCount) => {
  const getNiceInterval = (range: number, count: number): number => {
    const adjustedTickCount = Math.max(1, count - 1);
    const rawInterval = range / adjustedTickCount;
    const exponent = Math.floor(Math.log10(rawInterval));
    const mantissa = rawInterval / 10 ** exponent;

    let niceMantissa: number;
    if (mantissa < 1.5) {
      niceMantissa = 1;
    } else if (mantissa < 3) {
      niceMantissa = 2;
    } else if (mantissa < 7) {
      niceMantissa = 5;
    } else {
      niceMantissa = 10;
    }
    return niceMantissa * 10 ** exponent;
  };

  const domainRange = dataMax - dataMin;
  const niceInterval = getNiceInterval(domainRange, tickCount);

  // Determine the nice rounded domain boundaries that contain the input data range
  const domainMin = Math.floor(dataMin / niceInterval) * niceInterval;
  const domainMax = Math.ceil(dataMax / niceInterval) * niceInterval;
  const domain: [number, number] = [domainMin, domainMax];

  // Calculate the new, precise interval based on the fixed domain
  const finalInterval = (domainMax - domainMin) / (tickCount - 1);

  // Generate the ticks based on the new interval and domain
  const ticks: number[] = [];
  for (let i = 0; i < tickCount; i++) {
    const newTick = domainMin + i * finalInterval;
    // Round to prevent floating-point errors
    ticks.push(Math.round(newTick * 1000) / 1000);
  }

  return { domain, ticks };
};

export const buildTimeScale = (
  timeMin: number,
  timeMax: number,
  uniqueTimes: number[],
  tickCount: number
): { domain: [number, number]; ticks: number[] } => {
  const domain: [number, number] = [timeMin, timeMax];
  const sortedTimes = uniqueTimes.sort((a, b) => a - b);

  if (uniqueTimes.length <= tickCount) {
    return { domain, ticks: sortedTimes };
  }

  const ticks = Array.from(
    { length: tickCount },
    (_, i) => sortedTimes[Math.floor((i * (sortedTimes.length - 1)) / (tickCount - 1))]
  );

  return { domain, ticks };
};

// Given data with timestamps, returns the timestamps within range that represent the start, end, and middle of each month
export const getMonthTicks: ({
  data,
  dataKey,
  start,
  end
}: {
  data: any;
  dataKey: string;
  start: number;
  end: number;
}) => number[] = ({ data, dataKey, start, end }) => {
  const timestamps: number[] = [];

  for (const row of data) {
    const timestamp = row[dataKey];
    if (timestamp >= start && timestamp <= end) {
      const date = new Date(timestamp);
      const daysInMonth = getDaysInMonth(convertDateToUTC(date));
      const middleDateOfMonth = Math.ceil(daysInMonth / 2);

      // Add first and last
      if ([1, middleDateOfMonth, daysInMonth].includes(date.getUTCDate())) {
        if (date.getUTCDate() === middleDateOfMonth && daysInMonth % 2 === 0) {
          // If month has an odd number of days, and this timestamp is the middle date of the month,
          // Add a half day in milliseconds to it before adding to output
          timestamps.push(timestamp + MILLISECONDS_IN_A_DAY / 2);
        } else {
          timestamps.push(timestamp);
        }
      }
    }
  }

  return timestamps;
};
